import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MAX_MRCA_DEPTH,
  MrcaPersistedState,
  childPaths,
  defaultMrcaPersistedState,
  loadMrcaPersistedState,
  saveMrcaPersistedState,
  theoreticalPercentForPath,
  validateTheoreticalModel,
} from '../utils/mrcaLineageModel';
import {
  APPROX_AUTOSOMAL_TOTAL_CM,
  approximateGenomePercentFromCm,
  AssignedCmResult,
  buildDnaPainterMatchNameToIndividualId,
  computeAssignedCmByMrcaPath,
  normalizePainterSegment,
  NormalizedDnaSegment,
  MrcaPathAttributionKind,
} from '../utils/mrcaAssignedDna';
import './mrca-lineage-map.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Individual {
  id: string;
  first_name?: string;
  middle_names?: string;
  surname?: string;
  /** When set, DNA Painter segments with this match name count as linked to this person. */
  dna_painter_match_name?: string;
  /** Total shared cM on profile (e.g. FTDNA) — used if Painter segments do not sum. */
  shared_dna_cm?: number | string;
}

function formatIndividualName(ind: Individual): string {
  const parts = [ind.first_name, ind.middle_names, ind.surname].filter(Boolean).join(' ').trim();
  return parts || '(no name)';
}

function sortIndividualsForSelect(a: Individual, b: Individual): number {
  const sa = (a.surname || '').toLowerCase();
  const sb = (b.surname || '').toLowerCase();
  if (sa !== sb) return sa.localeCompare(sb);
  return formatIndividualName(a).localeCompare(formatIndividualName(b));
}

interface NodeRowProps {
  path: string;
  state: MrcaPersistedState;
  onPatch: (patch: Partial<MrcaPersistedState>) => void;
  individuals: Individual[];
  assignedBundle: AssignedCmResult;
}

const MrcaLineageNodeBlock: React.FC<NodeRowProps> = ({ path, state, onPatch, individuals, assignedBundle }) => {
  const predictedPct = theoreticalPercentForPath(path);
  const assignedCm = assignedBundle.byPath[path] ?? 0;
  const pathKind: MrcaPathAttributionKind = assignedBundle.pathKind[path] ?? 'unlinked';
  const linkedId = state.linkedIndividualIds[path] ?? '';
  const linkedPerson = linkedId ? individuals.find((i) => i.id === linkedId) : undefined;
  const displayName = state.displayNames[path] ?? '';
  const notes = state.notes[path] ?? '';
  const kids = childPaths(path, MAX_MRCA_DEPTH);
  const approxAssignedPct = approximateGenomePercentFromCm(assignedCm);
  const manualCm = state.manualAssignedCm[path];
  const manualCmStr =
    manualCm !== undefined && manualCm !== null && !Number.isNaN(manualCm) ? String(manualCm) : '';
  const predictedCmEst = Math.round((predictedPct / 100) * APPROX_AUTOSOMAL_TOTAL_CM * 10) / 10;

  const setTreePerson = (id: string) => {
    const nextLinks = { ...state.linkedIndividualIds };
    const nextNames = { ...state.displayNames };
    if (!id) {
      delete nextLinks[path];
      delete nextNames[path];
    } else {
      const ind = individuals.find((i) => i.id === id);
      nextLinks[path] = id;
      if (ind) nextNames[path] = formatIndividualName(ind);
    }
    onPatch({ linkedIndividualIds: nextLinks, displayNames: nextNames });
  };

  const setCustomName = (v: string) => {
    const nextLinks = { ...state.linkedIndividualIds };
    delete nextLinks[path];
    const next = { ...state.displayNames };
    if (v.trim()) next[path] = v.trim();
    else delete next[path];
    onPatch({ linkedIndividualIds: nextLinks, displayNames: next });
  };

  const setNotes = (v: string) => {
    const next = { ...state.notes };
    if (v.trim()) next[path] = v.trim();
    else delete next[path];
    onPatch({ notes: next });
  };

  const setManualCm = (v: string) => {
    const next = { ...state.manualAssignedCm };
    const t = v.trim();
    if (t === '') {
      delete next[path];
    } else {
      const n = parseFloat(t);
      if (!Number.isNaN(n) && n >= 0) next[path] = n;
    }
    onPatch({ manualAssignedCm: next });
  };

  return (
    <div className="mrca-lineage-node-block" style={{ marginLeft: Math.max(0, (path.length - 1) * 10) }}>
      <div className="mrca-lineage-node-row">
        <span className="mrca-lineage-node-path" title="M = maternal branch, P = paternal at each generation">
          {path}
        </span>
        <div className="mrca-lineage-node-dna-stack">
          <label className="mrca-lineage-node-dna-mini-label" htmlFor={`mrca-predict-pct-${path}`}>
            Genome % (theory)
          </label>
          <input
            id={`mrca-predict-pct-${path}`}
            className="mrca-lineage-node-input mrca-lineage-node-dna-input-readonly"
            type="text"
            readOnly
            value={`${predictedPct.toFixed(2)}%`}
            title="Simplified expected share of autosomal DNA from this ancestor"
            aria-label={`Predicted genome percent for lineage ${path}`}
          />
          <label className="mrca-lineage-node-dna-mini-label" htmlFor={`mrca-predict-cm-${path}`}>
            Est. cM if exact
          </label>
          <input
            id={`mrca-predict-cm-${path}`}
            className="mrca-lineage-node-input mrca-lineage-node-dna-input-readonly"
            type="text"
            readOnly
            value={`${predictedCmEst} cM`}
            title={`Rough cM if ${predictedPct.toFixed(2)}% of ~${APPROX_AUTOSOMAL_TOTAL_CM} cM autosomal total`}
            aria-label={`Estimated centimorgans if inheritance matched theory for ${path}`}
          />
        </div>
        <div className="mrca-lineage-node-name-cell">
          <label className="mrca-lineage-node-sr-only" htmlFor={`mrca-tree-select-${path}`}>
            Person from family tree for lineage {path}
          </label>
          <select
            id={`mrca-tree-select-${path}`}
            className="mrca-lineage-node-select"
            value={linkedId}
            onChange={(e) => setTreePerson(e.target.value)}
            aria-label={`Select person from tree for lineage ${path}`}
          >
            <option value="">— Choose from tree —</option>
            {linkedId && !linkedPerson && (
              <option value={linkedId}>
                {displayName ? `${displayName} (not in tree)` : 'Previously linked person (not in tree)'}
              </option>
            )}
            {individuals.map((ind) => (
              <option key={ind.id} value={ind.id}>
                {formatIndividualName(ind)}
              </option>
            ))}
          </select>
          <label className="mrca-lineage-node-sr-only" htmlFor={`mrca-custom-${path}`}>
            Custom label if not in tree, lineage {path}
          </label>
          <input
            id={`mrca-custom-${path}`}
            className="mrca-lineage-node-input mrca-lineage-node-input-custom"
            type="text"
            placeholder="Or custom label (not in tree)"
            value={linkedPerson ? '' : displayName}
            onChange={(e) => setCustomName(e.target.value)}
            disabled={!!linkedId && !!linkedPerson}
            aria-label={`Custom name for lineage ${path} when not linked to tree`}
          />
        </div>
        <div className="mrca-lineage-node-dna-stack">
          <label className="mrca-lineage-node-dna-mini-label" htmlFor={`mrca-assign-painter-${path}`}>
            DNA Painter cM
          </label>
          <input
            id={`mrca-assign-painter-${path}`}
            className="mrca-lineage-node-input mrca-lineage-node-dna-input-readonly"
            type="text"
            readOnly
            value={assignedCm > 0 ? String(assignedCm) : ''}
            placeholder={pathKind === 'suppressed_duplicate' ? '0' : '—'}
            title={
              pathKind === 'suppressed_duplicate'
                ? 'DNA Painter total for this person is counted on the longest MRCA path only (no double count).'
                : pathKind === 'linked_profile'
                  ? 'Using shared cM from this person’s profile (e.g. FTDNA total). Link DNA Painter match on their Individual record for segment-level totals and dedupe.'
                  : pathKind === 'no_data' && linkedId
                    ? 'No DNA Painter segments for this person yet. On their Individual record set “DNA Painter match” to the match name in your CSV, or enter shared cM on the profile.'
                    : 'Sum of DNA Painter cM for this person, deduped per chromosome (same rule as DNA flow).'
            }
            aria-label={`DNA Painter assigned centimorgans for lineage ${path}`}
          />
          {assignedCm > 0 && (
            <span className="mrca-lineage-node-assigned-pct">~{approxAssignedPct}% genome</span>
          )}
          {pathKind === 'suppressed_duplicate' && (
            <span className="mrca-lineage-node-assigned-hint">(counted on longer path)</span>
          )}
          {pathKind === 'linked_profile' && (
            <span className="mrca-lineage-node-assigned-hint">(from profile shared cM)</span>
          )}
          <label className="mrca-lineage-node-dna-mini-label" htmlFor={`mrca-assign-manual-${path}`}>
            Manual cM (optional)
          </label>
          <input
            id={`mrca-assign-manual-${path}`}
            className="mrca-lineage-node-input mrca-lineage-node-dna-input-manual"
            type="number"
            step="0.01"
            min={0}
            placeholder="e.g. segment total"
            value={manualCmStr}
            onChange={(e) => setManualCm(e.target.value)}
            aria-label={`Manual centimorgans for lineage ${path}`}
          />
        </div>
        <input
          className="mrca-lineage-node-notes"
          type="text"
          placeholder="Notes (e.g. match source, segment hypothesis)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          aria-label={`Notes for ${path}`}
        />
      </div>
      {kids.map((c) => (
        <MrcaLineageNodeBlock
          key={c}
          path={c}
          state={state}
          onPatch={onPatch}
          individuals={individuals}
          assignedBundle={assignedBundle}
        />
      ))}
    </div>
  );
};

export interface MrcaLineageMapProps {
  homeId: string;
}

const MrcaLineageMap: React.FC<MrcaLineageMapProps> = ({ homeId }) => {
  const [state, setState] = useState<MrcaPersistedState>(() => defaultMrcaPersistedState());
  const [individuals, setIndividuals] = useState<Individual[]>([]);
  const [individualsError, setIndividualsError] = useState<string | null>(null);
  const [dnaSegmentsRaw, setDnaSegmentsRaw] = useState<Record<string, unknown>[]>([]);
  const [segmentsError, setSegmentsError] = useState<string | null>(null);
  const [treeAssignLoading, setTreeAssignLoading] = useState(false);
  const [treeAssignError, setTreeAssignError] = useState<string | null>(null);

  useEffect(() => {
    setTreeAssignError(null);
    const loaded = loadMrcaPersistedState(homeId);
    setState(loaded);
    let cancelled = false;
    const hasLink = Object.values(loaded.linkedIndividualIds).some((id) => id && String(id).trim());
    if (hasLink) {
      return () => {
        cancelled = true;
      };
    }

    fetch(`${API_URL}/individuals/${encodeURIComponent(homeId)}/mrca_path_ancestors`)
      .then((res) => {
        if (!res.ok) throw new Error('ancestors');
        return res.json();
      })
      .then((data: { by_path?: Record<string, Individual | null> }) => {
        if (cancelled) return;
        const byPath = data.by_path || {};
        const nextLinks: Record<string, string> = {};
        const nextNames: Record<string, string> = {};
        Object.keys(byPath).forEach((path) => {
          const node = byPath[path];
          if (node && typeof node === 'object' && node.id) {
            nextLinks[path] = node.id;
            nextNames[path] = formatIndividualName(node);
          }
        });
        setState((prev) => {
          const next: MrcaPersistedState = {
            ...prev,
            linkedIndividualIds: { ...nextLinks },
            displayNames: { ...prev.displayNames, ...nextNames },
          };
          saveMrcaPersistedState(homeId, next);
          return next;
        });
      })
      .catch(() => {
        /* silent on first load — tree may be incomplete */
      });

    return () => {
      cancelled = true;
    };
  }, [homeId]);

  const reloadAssignmentsFromTree = useCallback(() => {
    setTreeAssignLoading(true);
    setTreeAssignError(null);
    fetch(`${API_URL}/individuals/${encodeURIComponent(homeId)}/mrca_path_ancestors`)
      .then((res) => {
        if (!res.ok) throw new Error('ancestors');
        return res.json();
      })
      .then((data: { by_path?: Record<string, Individual | null> }) => {
        const byPath = data.by_path || {};
        const nextLinks: Record<string, string> = {};
        const nextNames: Record<string, string> = {};
        Object.keys(byPath).forEach((path) => {
          const node = byPath[path];
          if (node && typeof node === 'object' && node.id) {
            nextLinks[path] = node.id;
            nextNames[path] = formatIndividualName(node);
          }
        });
        setState((prev) => {
          const next: MrcaPersistedState = {
            ...prev,
            linkedIndividualIds: { ...nextLinks },
            displayNames: nextNames,
          };
          saveMrcaPersistedState(homeId, next);
          return next;
        });
      })
      .catch(() =>
        setTreeAssignError(
          'Could not load ancestors from the tree. Check biological parent links (Bio Father / Bio Mother only).'
        )
      )
      .finally(() => setTreeAssignLoading(false));
  }, [homeId]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/individuals`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load individuals');
        return res.json();
      })
      .then((data: Individual[]) => {
        if (!cancelled) {
          setIndividuals([...data].sort(sortIndividualsForSelect));
          setIndividualsError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIndividuals([]);
          setIndividualsError('Could not load people from the family tree. Check the API and try again.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const q = encodeURIComponent(homeId);
    fetch(`${API_URL}/dna_painter/segments?home_id=${q}`)
      .then((res) => {
        if (!res.ok) throw new Error('segments');
        return res.json();
      })
      .then((data: Record<string, unknown>[]) => {
        if (!cancelled) {
          setDnaSegmentsRaw(Array.isArray(data) ? data : []);
          setSegmentsError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDnaSegmentsRaw([]);
          setSegmentsError('Could not load DNA Painter segments for assigned cM.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [homeId]);

  const matchNameToIndividualId = useMemo(
    () => buildDnaPainterMatchNameToIndividualId(individuals),
    [individuals]
  );

  const sharedCmFallbackByIndividualId = useMemo(() => {
    const out: Record<string, number> = {};
    for (let i = 0; i < individuals.length; i += 1) {
      const ind = individuals[i];
      const raw = ind.shared_dna_cm;
      const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
      if (Number.isFinite(n) && n > 0) out[ind.id] = n;
    }
    return out;
  }, [individuals]);

  const normalizedSegments: NormalizedDnaSegment[] = useMemo(() => {
    return dnaSegmentsRaw
      .map((raw) => normalizePainterSegment(raw as Record<string, string>, matchNameToIndividualId))
      .filter((s): s is NormalizedDnaSegment => s !== null);
  }, [dnaSegmentsRaw, matchNameToIndividualId]);

  const assignedBundle = useMemo(
    () =>
      computeAssignedCmByMrcaPath(normalizedSegments, state.linkedIndividualIds, {
        sharedCmFallbackByIndividualId,
      }),
    [normalizedSegments, state.linkedIndividualIds, sharedCmFallbackByIndividualId]
  );

  const onPatch = useCallback((patch: Partial<MrcaPersistedState>) => {
    setState((prev) => {
      const next: MrcaPersistedState = {
        displayNames: patch.displayNames ?? prev.displayNames,
        linkedIndividualIds: patch.linkedIndividualIds ?? prev.linkedIndividualIds,
        manualAssignedCm: patch.manualAssignedCm ?? prev.manualAssignedCm,
        notes: patch.notes ?? prev.notes,
      };
      saveMrcaPersistedState(homeId, next);
      return next;
    });
  }, [homeId]);

  const theoreticalCheck = useMemo(() => validateTheoreticalModel(MAX_MRCA_DEPTH), []);
  const approxTotalGenomePct = approximateGenomePercentFromCm(assignedBundle.totalAssignedCm);
  const totalManualCm = useMemo(() => {
    let t = 0;
    const m = state.manualAssignedCm;
    const keys = Object.keys(m);
    for (let i = 0; i < keys.length; i += 1) {
      const v = m[keys[i]];
      if (v !== undefined && v !== null && !Number.isNaN(v) && v > 0) t += v;
    }
    return Math.round(t * 100) / 100;
  }, [state.manualAssignedCm]);

  const reset = () => {
    const fresh = defaultMrcaPersistedState();
    setState(fresh);
    saveMrcaPersistedState(homeId, fresh);
  };

  return (
    <div className="mrca-lineage-map-root">
      <p className="mrca-lineage-map-legend">
        Map inheritance lines across six generations: each path codes <strong>M</strong> (maternal) and <strong>P</strong>{' '}
        (paternal) at every step. <strong>Name</strong> is chosen from the same people as the{' '}
        <Link to="/tree" className="mrca-lineage-map-tree-link">
          family tree
        </Link>
        . <strong>Predicted</strong> shows theory genome % and a rough cM if that % applied to ~{APPROX_AUTOSOMAL_TOTAL_CM}{' '}
        cM autosomal total. <strong>Assigned – DNA Painter cM</strong> uses imported Painter segments tied to that person
        (explicit link or same name as <strong>DNA Painter match</strong> on their Individual record), deduped like{' '}
        <Link to="/dna-flow" className="mrca-lineage-map-tree-link">DNA flow</Link>. If there are no linked segments,
        the map uses <strong>shared cM</strong> from their profile when set.{' '}
        <strong>Manual cM</strong> is for extra totals (e.g. segments you have not imported yet). Choose the{' '}
        <strong>home person</strong> above the map on the Chromosome Painter page. With no saved path assignments, use{' '}
        <strong>Reload tree assignments</strong> to fill paths by walking only <strong>biological</strong> parents{' '}
        (<strong>Bio Mother</strong> = M, <strong>Bio Father</strong> = P). Adoptive, step, and other parent types are ignored
        for DNA inheritance.
      </p>
      {individualsError && (
        <div className="mrca-lineage-map-fetch-error" role="alert">
          {individualsError}
        </div>
      )}
      {segmentsError && (
        <div className="mrca-lineage-map-fetch-error" role="alert">
          {segmentsError}
        </div>
      )}
      {treeAssignError && (
        <div className="mrca-lineage-map-fetch-error" role="alert">
          {treeAssignError}
        </div>
      )}

      <div
        className={`mrca-lineage-map-summary ${
          assignedBundle.warnings.length === 0 ? 'mrca-lineage-map-summary-ok' : 'mrca-lineage-map-summary-warn'
        }`}
      >
        <div>
          <h3 className="mrca-lineage-map-summary-title">Assigned DNA (from Painter)</h3>
          <p className="mrca-lineage-map-depth-table" style={{ marginBottom: 8 }}>
            Total deduped cM (MRCA paths): <strong>{assignedBundle.totalAssignedCm} cM</strong>
            {assignedBundle.totalAssignedCm > 0 && (
              <span>
                {' '}
                (~{approxTotalGenomePct}% of autosomal total vs ~{APPROX_AUTOSOMAL_TOTAL_CM} cM rule-of-thumb)
              </span>
            )}
          </p>
          {totalManualCm > 0 && (
            <p className="mrca-lineage-map-depth-table" style={{ marginBottom: 0 }}>
              Manual cM (sum of optional fields): <strong>{totalManualCm} cM</strong>
            </p>
          )}
          {assignedBundle.warnings.length === 0 ? (
            <p className="mrca-lineage-map-depth-table" style={{ color: '#81c784' }}>
              No duplicate tree person across multiple paths (or only one path each).
            </p>
          ) : (
            <ul className="mrca-lineage-map-summary-list">
              {assignedBundle.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="mrca-lineage-map-summary-title">Theoretical model (each generation = 100%)</h3>
          <table className="mrca-lineage-map-depth-table">
            <tbody>
              {theoreticalCheck.perDepth.map((row) => (
                <tr key={row.depth}>
                  <td style={{ paddingRight: 12 }}>Gen {row.depth}</td>
                  <td>{row.sum.toFixed(4)}% total</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mrca-lineage-map-actions">
        <button
          type="button"
          className="genealogy-btn-secondary"
          onClick={reloadAssignmentsFromTree}
          disabled={treeAssignLoading}
        >
          {treeAssignLoading ? 'Loading tree…' : 'Reload tree assignments'}
        </button>
        <button type="button" className="genealogy-btn-secondary" onClick={reset}>
          Reset map
        </button>
      </div>

      <div className="mrca-lineage-map-header-row">
        <span>Path</span>
        <span>Predicted DNA</span>
        <span>Person (tree)</span>
        <span>Assigned DNA</span>
        <span>Notes</span>
      </div>

      {(['M', 'P'] as const).map((p) => (
        <MrcaLineageNodeBlock
          key={p}
          path={p}
          state={state}
          onPatch={onPatch}
          individuals={individuals}
          assignedBundle={assignedBundle}
        />
      ))}
    </div>
  );
};

export default MrcaLineageMap;
